import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { changeMyPassword, getMyProfile, updateMyProfile } from '../../lib/api';
import { Eye, EyeOff, CreditCard, Users, UserRound, Building2, CalendarClock } from 'lucide-react';

const formatDate = (value) => {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not available';
  return parsed.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatPrice = (value) => {
  if (value === null || value === undefined) return 'Custom';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const formatLimit = (value) => (value === null || value === undefined ? 'Unlimited' : String(value));

const usageTone = (count, limit) => {
  if (limit === null || limit === undefined || !Number.isFinite(limit) || limit <= 0) return 'text-emerald-700';
  const ratio = count / limit;
  if (ratio >= 1) return 'text-red-600';
  if (ratio >= 0.8) return 'text-amber-600';
  return 'text-emerald-700';
};

const progressTone = (count, limit) => {
  if (limit === null || limit === undefined || !Number.isFinite(limit) || limit <= 0) return 'bg-emerald-500';
  const ratio = count / limit;
  if (ratio >= 1) return 'bg-red-500';
  if (ratio >= 0.8) return 'bg-amber-500';
  return 'bg-emerald-500';
};

const usageWidth = (count, limit) => {
  if (limit === null || limit === undefined || !Number.isFinite(limit) || limit <= 0) return '35%';
  return `${Math.min((count / limit) * 100, 100)}%`;
};

const statusBadgeClass = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'active') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300';
  if (normalized === 'inactive' || normalized === 'expired' || normalized === 'cancelled') {
    return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
  }
  if (normalized === 'trial') return 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300';
  return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300';
};

export default function AdminProfile() {
  const { user, token, updateProfile: updateAuthProfile } = useAuth();
  const location = useLocation();
  const isWhiteLabelProfile = location.pathname.startsWith('/whitelabel/profile');
  const basePath = isWhiteLabelProfile ? '/whitelabel/profile' : '/admin/profile';
  const isPassword = location.pathname.includes('/profile/password');

  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    phone: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        full_name: user.full_name || user.name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  useEffect(() => {
    const hydrateProfile = async () => {
      if (!token || !isWhiteLabelProfile) return;
      try {
        const resp = await getMyProfile(token);
        if (resp?.data) {
          updateAuthProfile(resp.data);
          setProfile({
            full_name: resp.data.full_name || resp.data.name || '',
            email: resp.data.email || '',
            phone: resp.data.phone || '',
          });
        }
      } catch {
        // Keep the cached auth context when refresh fails.
      }
    };
    hydrateProfile();
  }, [token, isWhiteLabelProfile, updateAuthProfile]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((p) => ({ ...p, [name]: value }));
  };

  const handleProfileSave = async () => {
    setSaving(true);
    try {
      if (token) {
        const resp = await updateMyProfile(token, profile);
        updateAuthProfile(resp?.data || profile);
      } else {
        updateAuthProfile(profile);
      }
      toast.success('Profile updated successfully.');
    } catch (err) {
      toast.error(err?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((p) => ({ ...p, [name]: value }));
  };

  const handlePasswordSubmit = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New password and confirm password do not match.');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters.');
      return;
    }
    setSaving(true);
    try {
      if (!token) {
        throw new Error('Please login again and retry.');
      }
      await changeMyPassword(token, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      updateAuthProfile({ must_change_password: false });
      toast.success('Password updated successfully.');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err?.message || 'Failed to update password.');
    } finally {
      setSaving(false);
    }
  };

  const workspace = user?.workspace || null;
  const currentPrice = workspace
    ? (workspace.billing_cycle === 'yearly' ? workspace.yearly_price : workspace.monthly_price)
    : null;
  const isSubscriptionInactive = workspace?.subscription_status && workspace.subscription_status !== 'active';
  const employeeCount = Number(workspace?.employee_count || 0);
  const employeeLimit = workspace?.employee_limit ?? null;
  const agentCount = Number(workspace?.agent_count || 0);
  const agentLimit = workspace?.agent_limit ?? null;

  return (
    <div className="space-y-6" data-testid="admin-profile">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            {isPassword ? 'Change Password' : 'My Profile'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isPassword ? 'Update your account password' : 'Update your profile details'}
          </p>
          {user?.must_change_password && isPassword && (
            <p className="text-sm text-amber-600 mt-2">
              You are signed in with a temporary password. Update it now to secure your account.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button asChild size="sm" variant={isPassword ? 'outline' : 'default'}>
            <Link to={basePath}>Profile</Link>
          </Button>
          <Button asChild size="sm" variant={isPassword ? 'default' : 'outline'}>
            <Link to={`${basePath}/password`}>Change Password</Link>
          </Button>
        </div>
      </div>

      {!isPassword ? (
        <div className="space-y-6">
          <div className={`grid gap-6 ${isWhiteLabelProfile ? 'xl:grid-cols-[1.2fr_0.8fr]' : ''}`}>
            <Card className="border border-border">
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Update your name, email and phone details.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={profile.full_name}
                      onChange={handleProfileChange}
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={profile.email}
                      onChange={handleProfileChange}
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={profile.phone}
                      onChange={handleProfileChange}
                      placeholder="Phone number"
                    />
                  </div>
                </div>

                {user?.role && (
                  <div className="flex items-center gap-3">
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                      {user.role === 'super_admin' ? 'Super Admin' : user.role}
                    </Badge>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleProfileSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to={isWhiteLabelProfile ? '/whitelabel/dashboard' : '/admin/dashboard'}>Cancel</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {isWhiteLabelProfile && (
              <Card className="border border-border bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 text-white">
                <CardHeader className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-white">
                        <CreditCard className="h-5 w-5 text-emerald-400" />
                        Subscription Overview
                      </CardTitle>
                      <CardDescription className="text-slate-300">
                        Your current workspace plan and renewal status.
                      </CardDescription>
                    </div>
                    <Badge className={statusBadgeClass(workspace?.subscription_status || 'inactive')}>
                      {workspace?.subscription_status || 'No Plan'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-2xl font-bold">{workspace?.pricing_plan_name || 'No active plan'}</h3>
                      {workspace?.pricing_plan_description ? (
                        <Badge variant="secondary" className="bg-white/10 text-slate-100 border-white/10">
                          {workspace.pricing_plan_description}
                        </Badge>
                      ) : null}
                    </div>
                    <p className="mt-2 text-sm text-slate-300">
                      {workspace
                        ? `${formatPrice(currentPrice)} / ${workspace.billing_cycle || 'billing cycle not set'}`
                        : 'Upgrade your workspace to unlock employee, agent and feature limits.'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                      <p className="text-slate-400">Plan Start</p>
                      <p className="mt-1 font-semibold">{formatDate(workspace?.subscription_started_at)}</p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                      <p className="text-slate-400">{isSubscriptionInactive ? 'Expiry Date' : 'Next Renewal'}</p>
                      <p className="mt-1 font-semibold">{formatDate(workspace?.subscription_expires_at)}</p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                      <p className="text-slate-400">Workspace Status</p>
                      <p className="mt-1 font-semibold capitalize">{workspace?.workspace_status || 'Not available'}</p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                      <p className="text-slate-400">Last Payment</p>
                      <p className="mt-1 font-semibold">{formatDate(workspace?.last_payment_at)}</p>
                    </div>
                  </div>

                  <Button asChild className="w-full bg-emerald-500 text-slate-950 hover:bg-emerald-400">
                    <Link to="/whitelabel/upgrade-plan">
                      {isSubscriptionInactive ? 'Renew / Upgrade Plan' : 'Upgrade Plan'}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {isWhiteLabelProfile && (
            <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-emerald-600" />
                    Usage & Limits
                  </CardTitle>
                  <CardDescription>Current plan utilisation for your workspace.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="space-y-3 rounded-xl border border-border/80 p-4">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <UserRound className="h-4 w-4 text-emerald-600" />
                        <span className="font-medium">Employees</span>
                      </div>
                      <span className={`font-semibold ${usageTone(employeeCount, employeeLimit)}`}>
                        {employeeCount} / {formatLimit(employeeLimit)}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100">
                      <div
                        className={`h-2 rounded-full transition-all ${progressTone(employeeCount, employeeLimit)}`}
                        style={{ width: usageWidth(employeeCount, employeeLimit) }}
                      />
                    </div>
                  </div>

                  <div className="space-y-3 rounded-xl border border-border/80 p-4">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-violet-600" />
                        <span className="font-medium">Agents</span>
                      </div>
                      <span className={`font-semibold ${usageTone(agentCount, agentLimit)}`}>
                        {agentCount} / {formatLimit(agentLimit)}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100">
                      <div
                        className={`h-2 rounded-full transition-all ${progressTone(agentCount, agentLimit)}`}
                        style={{ width: usageWidth(agentCount, agentLimit) }}
                      />
                    </div>
                  </div>

                  <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                    Upgrade before you hit plan limits to avoid interruption while adding new employees or agents.
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-sky-600" />
                    Workspace Details
                  </CardTitle>
                  <CardDescription>Useful account and workspace information for this admin account.</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-border/80 p-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Workspace Name</p>
                    <p className="mt-2 font-semibold">{workspace?.brand_name || 'Not available'}</p>
                  </div>
                  <div className="rounded-xl border border-border/80 p-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Workspace ID</p>
                    <p className="mt-2 font-semibold break-all">{workspace?.white_label_id || 'Not available'}</p>
                  </div>
                  <div className="rounded-xl border border-border/80 p-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Owner / Business</p>
                    <p className="mt-2 font-semibold">{workspace?.owner_name || profile.full_name || 'Not available'}</p>
                  </div>
                  <div className="rounded-xl border border-border/80 p-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Billing Cycle</p>
                    <p className="mt-2 font-semibold capitalize">{workspace?.billing_cycle || 'Not available'}</p>
                  </div>
                  <div className="rounded-xl border border-border/80 p-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Admin Created On</p>
                    <p className="mt-2 font-semibold">{formatDate(user?.created_at)}</p>
                  </div>
                  <div className="rounded-xl border border-border/80 p-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Support</p>
                    <p className="mt-2 font-semibold">info@finmatrixrq.com</p>
                  </div>
                  <div className="rounded-xl border border-border/80 p-4 sm:col-span-2">
                    <div className="flex items-start gap-3">
                      <CalendarClock className="mt-0.5 h-4 w-4 text-emerald-600" />
                      <div>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">Renewal Note</p>
                        <p className="mt-2 text-sm text-foreground">
                          Your plan remains accessible while the workspace status is active. Use the in-dashboard upgrade flow to renew or move to a higher plan without leaving the dashboard.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      ) : (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
            <CardDescription>Use a strong password and keep it secure.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <div className="relative">
                <Input
                  id="current_password"
                  name="current_password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  name="new_password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordForm.confirm_password}
                  onChange={handlePasswordChange}
                  placeholder="Re-enter new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handlePasswordSubmit}
                disabled={saving}
              >
                {saving ? 'Updating...' : 'Update Password'}
              </Button>
              <Button variant="outline" asChild>
                <Link to={basePath}>Cancel</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
